// SocialScreen — build an audience. Posting content costs energy and pays no cash
// up front, but it grows your follower count week after week. Hit a milestone and a
// brand deal unlocks — PASSIVE weekly income that arrives whether you post or not.
// The lesson: an audience is an ASSET. You build it once; it pays you forever.
//
// Same energy system as everything else. Brand-deal income lands in the week's
// cashflow → flows into the Payday harvest.

import {
  socialGrowth,
  brandDeals,
  nextMilestone,
  BRAND_TIERS,
  POST_ENERGY,
} from '@biz-kid/engine';
import { useDispatch, useGameState, selectHappiness, selectSocial } from '@biz-kid/store';
import { Card, Eyebrow, Button, Chip, money, Bar } from '../../ui/kit.js';
import { CartoonSocialScene as SocialScene } from '../../ui/cartoon.js';

export function SocialScreen({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch();
  const social = useGameState(selectSocial);
  const energy = useGameState(selectHappiness);

  const followers = social.followers;
  const { tiers, weeklyIncome } = brandDeals(followers);
  const next = nextMilestone(followers);
  const projected = socialGrowth(followers, true);
  const canPost = energy >= POST_ENERGY && !social.postedThisWeek;

  const start = () => dispatch({ kind: 'StartSocial' });
  const post = () => dispatch({ kind: 'PostContent' });

  return (
    <div className="bk-app" style={{ position: 'fixed', inset: 0, zIndex: 7, overflowY: 'auto' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: 'clamp(14px, 3vw, 26px)', paddingBottom: 90 }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <Eyebrow color="var(--brand)">Clip farm · rack up views</Eyebrow>
            <h1 style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>🎬 Clip Farm</h1>
          </div>
          <Button tone="ghost" size="sm" onClick={onClose}>← Home</Button>
        </header>

        {/* Voxel atmosphere — Miko on camera, audience hearts floating. */}
        <Card pad={0} style={{ overflow: 'hidden', marginBottom: 14 }}>
          <SocialScene />
        </Card>

        {!social.active ? (
          <Card>
            <p style={{ margin: 0, fontSize: 15, color: 'var(--text)', lineHeight: 1.6 }}>
              Post clips, rack up views, and once you go viral brands pay you <i>every week</i> — an audience is an asset.
            </p>
            <div style={{ marginTop: 16 }}>
              <Button tone="primary" size="lg" onClick={start}>Post your first clip →</Button>
            </div>
          </Card>
        ) : (
          <>
            {/* Follower count + post */}
            <Card style={{ marginBottom: 14 }}>
              <Eyebrow>Followers</Eyebrow>
              <div className="bk-tnum" style={{ fontSize: 44, fontWeight: 900, color: 'var(--text)', lineHeight: 1.05, marginTop: 4 }}>
                {followers.toLocaleString('en-US')}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                {social.postedThisWeek
                  ? <Chip tone="up">✓ Posted this week</Chip>
                  : <Chip tone="neutral">Next post → ~{(projected - followers).toLocaleString('en-US')} new</Chip>}
                <Chip tone={energy >= POST_ENERGY ? 'up' : 'down'}>⚡ Posting costs {POST_ENERGY} energy</Chip>
              </div>
              <div style={{ marginTop: 16 }}>
                <Button tone="primary" size="lg" onClick={post} disabled={!canPost}>
                  {social.postedThisWeek ? 'Already posted — wait for payday' : energy < POST_ENERGY ? 'Too tired to post' : '📸 Post content'}
                </Button>
              </div>
              {!social.postedThisWeek && (
                <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                  💡 Posting grows your audience faster the bigger it already is — that's compounding.
                  Skip a week and a few followers drift away.
                </p>
              )}
            </Card>

            {/* Milestone progress */}
            <Card style={{ marginBottom: 14 }}>
              <Eyebrow color="var(--brand)">Next milestone</Eyebrow>
              {next ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '8px 0 8px' }}>
                    <span style={{ fontSize: 14, color: 'var(--text-2)' }}>
                      {followers.toLocaleString('en-US')} / {next.milestone.toLocaleString('en-US')}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--brand)' }}>unlocks {money(next.weekly)}/wk</span>
                  </div>
                  <Bar frac={Math.min(1, followers / next.milestone)} color="var(--brand)" />
                </>
              ) : (
                <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--up)', fontWeight: 700 }}>
                  🏆 Every brand deal unlocked — you've built a real audience asset.
                </p>
              )}
            </Card>

            {/* Brand deals */}
            <Card style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <Eyebrow color="var(--up)">Brand deals</Eyebrow>
                <span style={{ fontSize: 14, fontWeight: 900, color: weeklyIncome > 0 ? 'var(--up)' : 'var(--text-3)' }}>
                  {money(weeklyIncome)}/wk passive
                </span>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {BRAND_TIERS.map((t) => {
                  const unlocked = tiers.includes(t);
                  return (
                    <div
                      key={t.milestone}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 12px', borderRadius: 12,
                        border: '1px solid var(--border)',
                        background: unlocked ? 'var(--brand-soft)' : 'var(--surface-2)',
                        opacity: unlocked ? 1 : 0.7,
                      }}
                    >
                      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
                        {unlocked ? '✅' : '🔒'} {t.milestone.toLocaleString('en-US')} followers
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: unlocked ? 'var(--up)' : 'var(--text-3)' }}>
                        {money(t.weekly)}/wk
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Lesson */}
            <Card>
              <Eyebrow color="var(--warn)">Audience as an asset</Eyebrow>
              <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
                💡 You did the work of posting <i>once</i>, but a brand deal pays you <b style={{ color: 'var(--text)' }}>every
                week after</b> — even on weeks you don't post. That's the magic of an asset: build it once, it pays you
                forever. An audience is one of the few assets a kid can start building today.
              </p>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
